import { useState, useEffect } from "react";
import { useSnackbar } from "notistack";
import { useNavigate, useParams } from "react-router-dom";
import {
  Card,
  CircularProgress,
  Grid,
  FormControlLabel,
  Typography,
  TextField,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useSelector } from "react-redux";
import axios from "axios";
import DeleteIcon from "@mui/icons-material/Delete";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import BookmarkIcon from "@mui/icons-material/Bookmark";

// Components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDBox from "components/MDBox";
import Checkbox from "@mui/material/Checkbox";
import SelectArtist from "components/SelectArtist/SelectArtist";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import FormGroup from "@mui/material/FormGroup";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";

import MDBadge from "components/MDBadge";
import ResizableDataGrid from "components/ResizableDataGrid";
import {
  DeleteForeverOutlined,
  DeleteOutlineOutlined,
} from "@mui/icons-material";

import { format } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import * as XLSX from "xlsx";
import Autocomplete from "@mui/material/Autocomplete";
import MDDialog from "components/MDDialog/MDDialog";

const seriesData = [
  { series_id: 1, series_name: "PRO" },
  { series_id: 2, series_name: "PRN" },
  { series_id: 3, series_name: "DAG" },
  { series_id: 4, series_name: "DAP" },
  { series_id: 5, series_name: "BMC" },
];

function StoreSale() {
  const auth = localStorage.getItem("authToken");
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [values, setValues] = useState();
  const [customerName, setCustomerName] = useState("");
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportFromDate, setExportFromDate] = useState("");
  const [exportToDate, setExportToDate] = useState("");

  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setValues(e.target.value);
  };
  // Function to convert UTC to IST
  const convertUTCtoIST = (utcDate) => {
    const timeZone = "UTC";
    const zonedDate = toZonedTime(utcDate, timeZone);
    return format(zonedDate, "yyyy-MM-dd HH:mm:ss");
  };

  const formatOrderDateDisplay = (utcDate) => {
    if (!utcDate) return "";
    try {
      const timeZone = "UTC";
      const zonedDate = toZonedTime(new Date(utcDate), timeZone);
      return format(zonedDate, "dd MMM yyyy, h:mm a");
    } catch {
      return "";
    }
  };

  const [stores, setStores] = useState([]);

  const getOrderDateStr = (row) => {
    const d = row?.order?.order_date;
    if (!d) return "";
    try {
      return format(new Date(d), "yyyy-MM-dd");
    } catch {
      return "";
    }
  };

  const getStoresByDateRange = (from, to) =>
    stores.filter((row) => {
      const orderDateStr = getOrderDateStr(row);
      if (!from && !to) return true;
      if (!orderDateStr) return false;
      if (from && orderDateStr < from) return false;
      if (to && orderDateStr > to) return false;
      return true;
    });

  const toFilenameDate = (yyyyMmDd) => {
    if (!yyyyMmDd) return "";
    try {
      return format(new Date(yyyyMmDd), "dMMMyyyy").toLowerCase();
    } catch {
      return "";
    }
  };

  const handleExportFromDialog = () => {
    let filtered = getStoresByDateRange(exportFromDate, exportToDate);
    if (!filtered.length) {
      enqueueSnackbar("No data in the selected date range.", { variant: "warning" });
      return;
    }
    filtered = [...filtered].sort((a, b) => {
      const dateA = a?.order?.order_date ? new Date(a.order.order_date).getTime() : 0;
      const dateB = b?.order?.order_date ? new Date(b.order.order_date).getTime() : 0;
      return dateA - dateB;
    });
    const rows = filtered.map((row) => {
      const orderDate = row?.order?.order_date;
      let orderDateStr = "";
      if (orderDate) {
        try {
          orderDateStr = format(new Date(orderDate), "dd-MM-yyyy");
        } catch {
          orderDateStr = "";
        }
      }
      return {
      "Order ID": row?.order?.order_id ?? "",
      "Order Date": orderDateStr,
      "Customer Name":
        (row?.customer_name ?? row?.order?.customer_name ?? "").trim() || "NA",
      "Catalogue IDs":
        row?.catalogues?.map((c) => c.catalogue_id).join(", ") || "",
      "Status": row?.order?.status ?? "",
    };
    });
    const todayStr = toFilenameDate(format(new Date(), "yyyy-MM-dd"));
    const fromStr = toFilenameDate(exportFromDate);
    const toStr = toFilenameDate(exportToDate);
    const fileName =
      fromStr && toStr
        ? `StoreSale-${fromStr}-${toStr}.xlsx`
        : fromStr
          ? `StoreSale-${fromStr}-${todayStr}.xlsx`
          : toStr
            ? `StoreSale-${toStr}.xlsx`
            : `StoreSale-${todayStr}.xlsx`;
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Store Sales");
    XLSX.writeFile(wb, fileName);
    enqueueSnackbar("Excel exported successfully", { variant: "success" });
    setExportDialogOpen(false);
  };

  const columns = [
    {
      field: "order_id",
      headerName: "Order ID",
      width: 100,
      valueGetter: (params) => params.row.order.order_id,
    },
    {
      field: "order_date",
      headerName: "Order Date",
      width: 200,
      valueGetter: (params) => formatOrderDateDisplay(params.row?.order?.order_date),
      align: "center",
      headerAlign: "center",
    },
    {
      field: "customer_name",
      headerName: "Customer Name",
      width: 180,
      valueGetter: (params) => {
        const name =
          params.row?.customer_name ??
          params.row?.order?.customer_name ??
          "";
        return name.trim() || "NA";
      },
    },
    {
      field: "catalogue_ids",
      headerName: "Catalogue IDs",
      width: 400,
      valueGetter: (params) => 
        params.row?.catalogues?.map(catalogue => catalogue.catalogue_id).join(', ') || '',
      align: "center",
      headerAlign: "center",
    },
    {
      field: "status",
      headerName: "Status",
      width: 200,
      renderCell: (params) => (
        <MDBadge
          badgeContent={params.row?.order?.status}
          container
          color={params.row?.order?.status === "store sales" ? "success" : "error"}
        />
      ),
    },
  ];
  useEffect(() => {
    getUsers();
  }, []);
  const getUsers = async () => {
    try {
      setLoading(true);
      const res = await axios.post(
        `${process.env.REACT_APP_BASEURL}/order/getallstoresales`,
        null,
        {
          headers: { Authorization: `Bearer ${auth}` },
        }
      );
      if (res.data.success) {
        const list = Object.values(res.data.data).map((item, index) => ({
          ...item,
          id: item.catalogue_id || index,
        }));
        list.sort((a, b) => {
          const dateA = a?.order?.order_date ? new Date(a.order.order_date).getTime() : 0;
          const dateB = b?.order?.order_date ? new Date(b.order.order_date).getTime() : 0;
          return dateB - dateA;
        });
        setStores(list);
      } else {
        if (res.data.data === "no record found") {
          enqueueSnackbar(res.data.data, { variant: "info" });
        } else {
          console.log("Error Occurred");
          enqueueSnackbar(res.data.message, { variant: "error" });
        }
      }
    } catch (error) {
      console.log(error);
      enqueueSnackbar("Error occurred. Please try again.", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };
  console.log(stores)
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!values) {
      enqueueSnackbar("Please Provide All The Details !!!", {
        variant: "error",
      });
      return;
    }
    setLoading(true);


    await axios({
      method: "put",
      url: `${process.env.REACT_APP_BASEURL}/order/orderofflinewithcatids`,
      data: {
        cat_ids: values.split(","),
        ...(customerName?.trim() && { customer_name: customerName.trim() }),
      },
      headers: {
        Authorization: `Bearer ${auth}`,
      },
    })
      .then((res) => {
        if (res.data.success) {
          enqueueSnackbar("Operation successful", {
            variant: "success",
          });
          setValues("");
          setCustomerName("");
          navigate(`/store-sale`);
          getUsers();
        } else {
          enqueueSnackbar(res.data.message, { variant: "error" });
        }
      })
      .catch((err) => {
        console.error("Error occurred:", err);
        enqueueSnackbar("Error occurred. Please try again.", {
          variant: "error",
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };
  
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Enter") {
        handleSubmit(e);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [values]); 

  return (
    <DashboardLayout>
      <DashboardNavbar hidebreadcrumbTitle />

      <MDBox pt={3} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="h6" color="white">
                  Store Sale
                </MDTypography>
                <MDButton
                  variant="outlined"
                  color="white"
                  onClick={() => {
                    setExportFromDate("");
                    setExportToDate("");
                    setExportDialogOpen(true);
                  }}
                >
                  Export to Excel
                </MDButton>
              </MDBox>

              <Dialog
                open={exportDialogOpen}
                onClose={() => setExportDialogOpen(false)}
                maxWidth="sm"
                fullWidth
                aria-labelledby="export-dialog-title"
              >
                <DialogTitle id="export-dialog-title">
                  Export Store Sales to Excel
                </DialogTitle>
                <DialogContent>
                  <MDBox pt={1} pb={2}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <MDInput
                          type="date"
                          label="From date"
                          value={exportFromDate}
                          onChange={(e) => setExportFromDate(e.target.value)}
                          fullWidth
                          margin="dense"
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <MDInput
                          type="date"
                          label="To date"
                          value={exportToDate}
                          onChange={(e) => setExportToDate(e.target.value)}
                          fullWidth
                          margin="dense"
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                    </Grid>
                    <MDTypography variant="caption" color="text" display="block" sx={{ mt: 1 }}>
                      Leave both empty to export all data.
                    </MDTypography>
                  </MDBox>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                  <MDButton
                    variant="outlined"
                    color="dark"
                    onClick={() => setExportDialogOpen(false)}
                  >
                    Cancel
                  </MDButton>
                  <MDButton
                    variant="gradient"
                    color="success"
                    onClick={handleExportFromDialog}
                  >
                    Export
                  </MDButton>
                </DialogActions>
              </Dialog>

              <MDBox p={3} component="form" role="form">
                <MDBox mb={2}>
                  <MDInput
                    type="text"
                    label="Customer name (optional)"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    fullWidth
                    margin="dense"
                  />
                </MDBox>
                <MDInput
                  label="Enter (,) separated list of catalogue IDs"
                  value={values}
                  multiline
                  rows={3}
                  onChange={handleChange}
                  fullWidth
                />
              </MDBox>

              <Grid
                container
                direction="row"
                justifyContent="flex-end"
                p={2}
               
              >
                <MDButton
                  variant="gradient"
                  color="info"
                  onClick={handleSubmit}
                  disabled={loading}
                >
                  {loading ? (
                    <CircularProgress size={20} sx={{ color: "inherit" }} />
                  ) : (
                    "Create"
                  )}
                </MDButton>
              </Grid>
              <MDBox pt={3}>
                {loading ? (
                  <CircularProgress
                    style={{
                      marginTop: "80px",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "100vh",
                    }}
                    color="inherit"
                  />
                ) : (
                  <>
                    {stores && (
                      <MDBox sx={{ height: 400 }} p={2}>
                        <ResizableDataGrid
                          // getRowId={(row) => row.user_id}
                          rows={stores}
                          columns={columns}
                          initialState={{
                            pagination: {
                              paginationModel: {
                                pageSize: 100,
                              },
                            },
                          }}
                         
                          disableRowSelectionOnClick
                        />
                      </MDBox>
                    )}
                  </>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
}

export default StoreSale;
